'use client';

import type { FC } from 'react';
import { useRef, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmPopup } from '../../hooks/useConfirmPopup';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { ScreenContent, ScreenTabBar, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { Spinner } from '../../components/ui/Spinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Text, EmptyState, SegmentedControl, Button, ButtonGroup, OverlayBadge } from '../../components/ui';
import type { Segment } from '../../components/ui/SegmentedControl';
import { useNavigation } from '../../contexts/NavigationContext';
import { useHaptic } from '../../hooks/useHaptic';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { pluralize } from '../../i18n/pluralize';
import { formatCreditsNumber } from '../../utils/formatters';
import { Credits } from '../../components/Credits';
import { showErrorToast, showInfoToast } from '../../components/Toast';
import { formatApiError } from '../../utils/apiError';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { DesktopSidebarPanel } from '../../components/desktop/DesktopSidebarPanel';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import {
  useInventoryInfinite,
  useSellAllItems,
  useSellItems,
  useAvailableItemsInfo,
  parseInventoryFilter,
  type InventoryFilter,
} from '../../queries/useInventoryQueries';
import { ItemListItem, ITEM_CARD_HEIGHT, ITEM_GRID_GAP, ITEM_GRID_MIN_COLUMN } from '../../components/ItemListItem';
import { VirtualGrid } from '../../components/VirtualGrid';
import { ItemGridSkeleton } from '../../components/ItemGridSkeleton';
import { ItemPrice, useSteamPriceAllowed } from '../../components/ItemPrice';
import { getStatusLabel } from './utils';
import { cn } from '../../utils/cn';
import { UserInventoryItemResponseStatusEnum } from '../../services/generated/Api';
import type { UserInventoryItemResponse } from '../../services/api';
import translations from './InventoryScreen.translations';

interface InventoryScreenParams {
  filter?: InventoryFilter;
}

interface InventoryScreenProps {
  params?: InventoryScreenParams;
}

// WHY: держать в такт с transition shot-inventory-tile в animations.css
const TILE_EXIT_MS = 180;

const TILE_TRAVEL_MS = 240;
const TILE_TRAVEL_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

const NO_IDS: ReadonlySet<string> = new Set<string>();

const SelectedCheck: FC = () => (
  <span className="w-6 h-6 rounded-full bg-accent-blue text-white flex items-center justify-center shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const UnselectedCircle: FC = () => (
  <span className="w-6 h-6 rounded-full border-2 border-white/70 bg-black/30 shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
);

const SoldAmount: FC<{ amount: number }> = ({ amount }) => (
  <span className="inline-flex items-center gap-0.5 font-semibold text-price">
    +<Credits amount={amount} iconSize={11} />
  </span>
);

export const InventoryScreen: FC<InventoryScreenProps> = ({ params }) => {
  const { t, lang } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const { notification } = useHaptic();
  const steamPriceAllowed = useSteamPriceAllowed();
  const isDesktop = useIsDesktop();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  const [activeFilter, setActiveFilter] = useState<InventoryFilter>(
    parseInventoryFilter(params?.filter)
  );

  const paramsFilter = parseInventoryFilter(params?.filter);
  useEffect(() => {
    if (paramsFilter !== activeFilter) {
      setActiveFilter(paramsFilter);
    }
  }, [paramsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInventoryInfinite(activeFilter);

  const sellAllMutation = useSellAllItems();
  const sellItemsMutation = useSellItems();
  const availableInfoQuery = useAvailableItemsInfo(isDesktop);

  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const uiMotionEnabled = useUiMotionEnabled();

  const [soldIds, setSoldIds] = useState<ReadonlySet<string>>(NO_IDS);
  const [leavingIds, setLeavingIds] = useState<ReadonlySet<string>>(NO_IDS);
  const [frozenItems, setFrozenItems] = useState<UserInventoryItemResponse[] | null>(null);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const flipOriginsRef = useRef<Map<string, { x: number; y: number }> | null>(null);

  useEffect(() => {
    setSoldIds(NO_IDS);
    setLeavingIds(NO_IDS);
    setFrozenItems(null);
    flipOriginsRef.current = null;
  }, [activeFilter]);

  useEffect(() => {
    queryClient.refetchQueries({
      queryKey: ['inventory', activeFilter],
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadedItems = data?.pages.flatMap((page) => page.items) ?? [];
  const gridItems =
    soldIds.size === 0
      ? loadedItems
      : loadedItems.filter((item) => !soldIds.has(item.id) || leavingIds.has(item.id));
  const gridItemsRef = useRef(gridItems);
  gridItemsRef.current = gridItems;

  const items = frozenItems ?? gridItems;

  useInfiniteScroll({
    hasMore: hasNextPage,
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    threshold: 200,
    scrollRef,
  });

  const showPopup = useConfirmPopup();

  const availableItems = items.filter(
    (i) => i.status === UserInventoryItemResponseStatusEnum.AVAILABLE,
  );
  const hasAvailable = availableItems.length > 0;
  const canSell = activeFilter === 'available' && hasAvailable;
  const selectedTotal = items.reduce(
    (sum, i) => (selectedIds.has(i.id) ? sum + (i.acquiredPriceInCredits || 0) : sum),
    0,
  );


  // WHY: мерить от бокса сетки, не от вьюпорта — у низа браузер зажимает scrollTop, и все выжившие плитки «сдвинулись»
  const captureTilePositions = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const origin = grid.getBoundingClientRect();
    const positions = new Map<string, { x: number; y: number }>();
    grid.querySelectorAll<HTMLElement>('[data-tile-id]').forEach((tile) => {
      const id = tile.dataset.tileId;
      if (!id) return;
      const rect = tile.getBoundingClientRect();
      positions.set(id, { x: rect.left - origin.left, y: rect.top - origin.top });
    });
    flipOriginsRef.current = positions;
  }, []);

  useLayoutEffect(() => {
    const origins = flipOriginsRef.current;
    if (!origins) return;
    flipOriginsRef.current = null;
    const grid = gridRef.current;
    if (!grid) return;
    const origin = grid.getBoundingClientRect();
    grid.querySelectorAll<HTMLElement>('[data-tile-id]').forEach((tile) => {
      const id = tile.dataset.tileId;
      const from = id ? origins.get(id) : undefined;
      if (!from) return;
      const rect = tile.getBoundingClientRect();
      const dx = from.x - (rect.left - origin.left);
      const dy = from.y - (rect.top - origin.top);
      if (dx === 0 && dy === 0) return;
      tile.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0px, 0px)' }],
        { duration: TILE_TRAVEL_MS, easing: TILE_TRAVEL_EASING },
      );
    });
  });

  useEffect(() => {
    if (leavingIds.size === 0) return;
    const timer = window.setTimeout(() => {
      captureTilePositions();
      setFrozenItems(null);
      setLeavingIds(NO_IDS);
    }, TILE_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [leavingIds, captureTilePositions]);

  const beginDeparture = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const departing = new Set(ids);
      setSoldIds((prev) => new Set([...prev, ...departing]));
      if (!uiMotionEnabled) return;
      setFrozenItems(gridItemsRef.current);
      setLeavingIds(departing);
    },
    [uiMotionEnabled],
  );

  const exitSelect = useCallback(() => {
    setSelecting(false);
    setSelectedIds(new Set());
  }, []);

  const toggleItem = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const soldToastTitle = useCallback(
    (count: number) =>
      t('sellAllSuccess', {
        count,
        word: pluralize(count, { one: t('itemOne'), few: t('itemFew'), many: t('itemMany') }, lang),
      }),
    [t, lang],
  );

  const itemsInStockLabel = useCallback(
    (count: number) =>
      t('itemsInStock', {
        count,
        word: pluralize(count, { one: t('itemOne'), few: t('itemFew'), many: t('itemMany') }, lang),
      }),
    [t, lang],
  );

  const handleSellSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const result = await showPopup({
      title: t('confirmSellAllTitle'),
      message: t('confirmSellAllMessage', {
        count: String(selectedIds.size),
        amount: formatCreditsNumber(selectedTotal),
      }),
      buttons: [
        { id: 'cancel', type: 'cancel' },
        { id: 'confirm', type: 'default', text: t('confirmSellSelectedConfirm') },
      ],
    });
    if (result !== 'confirm') return;
    const sold = [...selectedIds];
    let response;
    try {
      response = await sellItemsMutation.mutateAsync(sold);
    } catch (err) {
      notification('error');
      console.error('Failed to sell selected items:', formatApiError(err));
      showErrorToast(t('sellAllError'));
      return;
    }
    notification('success');
    showInfoToast(
      soldToastTitle(response.soldCount),
      <SoldAmount amount={response.totalAmountInCredits} />,
    );
    exitSelect();
    beginDeparture(sold);
  }, [
    selectedIds,
    selectedTotal,
    showPopup,
    t,
    sellItemsMutation,
    notification,
    soldToastTitle,
    exitSelect,
    beginDeparture,
  ]);

  const renderSellButton = (size: 's' | 'l') =>
    selecting ? (
      <>
        <Button
          size={size}
          stretched
          onClick={handleSellSelected}
          disabled={selectedIds.size === 0}
          loading={sellItemsMutation.isPending}
        >
          {selectedIds.size > 0
            ? `${t('sellSelected')} · ${formatCreditsNumber(selectedTotal)}`
            : t('sellSelected')}
        </Button>
        <Button size={size} stretched variant="secondary" onClick={exitSelect}>
          {t('confirmSellAllCancel')}
        </Button>
      </>
    ) : (
      <>
        <Button
          size={size}
          stretched
          onClick={handleSellAll}
          loading={sellAllMutation.isPending || availableInfoQuery.isFetching}
        >
          {t('sellAll')}
        </Button>
        {hasAvailable && (
          <Button size={size} stretched variant="secondary" onClick={() => setSelecting(true)}>
            {t('select')}
          </Button>
        )}
      </>
    );

  const handleSellAll = useCallback(async () => {
    const { data: info } = await availableInfoQuery.refetch();

    if (!info || info.count === 0) {
      await showPopup({
        title: t('confirmSellAllTitle'),
        message: t('noItemsToSell'),
        buttons: [{ id: 'ok', type: 'default', text: 'OK' }],
      });
      return;
    }

    const result = await showPopup({
      title: t('confirmSellAllTitle'),
      message: t('confirmSellAllMessage', {
        count: info.count.toString(),
        amount: formatCreditsNumber(info.totalCredits),
      }),
      buttons: [
        { id: 'cancel', type: 'cancel' },
        { id: 'confirm', type: 'default', text: t('confirmSellAllConfirm') },
      ],
    });

    if (result !== 'confirm') return;
    const sold = availableItems.map((i) => i.id);
    let response;
    try {
      response = await sellAllMutation.mutateAsync();
    } catch (err) {
      notification('error');
      console.error('Failed to sell all items:', formatApiError(err));
      showErrorToast(t('sellAllError'));
      return;
    }
    notification('success');
    showInfoToast(
      soldToastTitle(response.soldCount),
      <SoldAmount amount={response.totalAmountInCredits} />,
    );
    beginDeparture(sold);
  }, [
    availableInfoQuery,
    showPopup,
    t,
    sellAllMutation,
    notification,
    soldToastTitle,
    availableItems,
    beginDeparture,
  ]);

  const handleFilterChange = useCallback((filter: InventoryFilter) => {
    setActiveFilter(filter);
    exitSelect();
    navigation.updateCurrentParams({ filter });
    queryClient.refetchQueries({
      queryKey: ['inventory', filter],
    });
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [navigation, queryClient, exitSelect]);

  if (error && items.length === 0) {
    return (
      <>
        <ScreenTabBar />
        <ScreenContent scrollRef={scrollRef} scrollKey="inventory">
          <ErrorMessage
            error={t('loadingError')}
            onRetry={() => refetch()}
          />
        </ScreenContent>
      </>
    );
  }

  const inventoryTabs: Segment<InventoryFilter>[] = [
    { id: 'available', label: t('filtersAvailable') },
    { id: 'sold', label: t('filtersSold') },
    { id: 'withdrawn', label: t('filtersWithdrawn') },
  ];

  const itemsGrid = (
    <>
      {isLoading && items.length === 0 ? (
        <ItemGridSkeleton />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('empty')}
          description={t('emptyHint')}
          action={
            <Button size="l" onClick={() => navigation.navigate('cases')}>
              {t('emptyCta')}
            </Button>
          }
        />
      ) : (
        <>
          <VirtualGrid
            items={items}
            innerRef={gridRef}
            scrollRef={scrollRef}
            rowHeight={ITEM_CARD_HEIGHT}
            gap={ITEM_GRID_GAP}
            minColumnWidth={ITEM_GRID_MIN_COLUMN}
            className="pt-2"
            footer={
              isFetchingNextPage ? (
                <div className="flex justify-center p-5">
                  <Spinner size="s" />
                </div>
              ) : null
            }
            renderItem={(item: UserInventoryItemResponse) => {
            const statusInfo = getStatusLabel(item.status, t);
            const isAvailable = item.status === UserInventoryItemResponseStatusEnum.AVAILABLE;
            const selectable = selecting && isAvailable;
            const selected = selectedIds.has(item.id);
            const leaving = leavingIds.has(item.id);
            const ringed = (selecting && selected) || leaving;
            const topRight = selectable
              ? selected
                ? <SelectedCheck />
                : <UnselectedCircle />
              : leaving
                ? <SelectedCheck />
                : undefined;
            return (
              <div
                key={item.id}
                data-tile-id={item.id}
                data-leaving={leaving ? '' : undefined}
                className="shot-inventory-tile"
              >
              <ItemListItem
                item={{
                  ...item.item,
                  imageUrl: item.item.imageUrl || '',
                  source: item.source,
                }}
                onClick={
                  selecting
                    ? selectable
                      ? () => toggleItem(item.id)
                      : undefined
                    : () => navigation.navigate('inventory-item', { itemId: item.id })
                }
                className={cn(
                  'transition-all',
                  ringed && 'ring-[3px] ring-inset ring-accent-blue',
                  selecting && !isAvailable && 'opacity-40',
                )}
                topRightContent={topRight}
              >
                <div className="flex flex-col items-start gap-1.5">
                  <OverlayBadge>
                    <Text className="text-xs font-semibold uppercase tracking-[0.5px]" style={{ color: statusInfo.color }}>
                      {statusInfo.text}
                    </Text>
                  </OverlayBadge>
                  <OverlayBadge>
                    <ItemPrice credits={item.acquiredPriceInCredits || 0} variant="cardPrice" />
                  </OverlayBadge>
                  {steamPriceAllowed && item.item.steamPrice != null && (
                    <OverlayBadge>
                      <ItemPrice steamPrice={item.item.steamPrice} steamIconSize={11} variant="steamPrice" />
                    </OverlayBadge>
                  )}
                </div>
              </ItemListItem>
              </div>
            );
          }}
          />
        </>
      )}
    </>
  );

  if (isDesktop) {
    return (
      <ScreenContent scrollRef={scrollRef} scrollKey="inventory">
        <div className="flex gap-6 p-6">

          <DesktopSidebarPanel width={240} className="flex flex-col overflow-hidden">
            <div className="px-2 py-2">
              {inventoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`flex items-center w-full px-3 py-2.5 rounded-lg border-none cursor-pointer transition-colors text-start text-sm font-medium outline-none ${
                    activeFilter === tab.id
                      ? 'bg-white/[0.08] text-white'
                      : 'bg-transparent text-white/50 hover:bg-white/[0.04] hover:text-white/70'
                  }`}
                  onClick={() => handleFilterChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {availableInfoQuery.data && (
              <div className="px-4 py-3 border-t border-white/[0.04] flex flex-col items-center gap-0.5">
                <Credits amount={availableInfoQuery.data.totalCredits} iconSize={14} className="text-base font-bold text-white" />
                <span className="text-[10px] text-white/25">{itemsInStockLabel(availableInfoQuery.data.count)}</span>
              </div>
            )}


            {canSell && <div className="px-4 pb-4">{renderSellButton('s')}</div>}
          </DesktopSidebarPanel>


          <div className="flex-1 min-w-0">
            {itemsGrid}
          </div>
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenTabBar />
      <ScreenFixedTop title={t('title')}>
        <SegmentedControl
          segments={inventoryTabs}
          value={activeFilter}
          onChange={handleFilterChange}
          className="px-4 py-2"
        />
      </ScreenFixedTop>
      <ScreenContent scrollRef={scrollRef} scrollKey="inventory">
        {itemsGrid}
      </ScreenContent>
      {canSell && (
        <ScreenFixedBottom>
          <ButtonGroup direction="horizontal">
            {renderSellButton('l')}
          </ButtonGroup>
        </ScreenFixedBottom>
      )}
    </>
  );
};
